import { mat4, vec4 } from 'gl-matrix'
import type { BlockPos } from './core/BlockPos.js'
import type { Mesh } from './render/Mesh.js'
import type { Quad } from './render/Quad.js'
import type { Vector } from './math/Vector.js'

export type RayTraceVec3 = [number, number, number]

export interface RayTraceRay {
	origin: RayTraceVec3
	direction: RayTraceVec3
}

export interface RayTraceHit {
	blockPos: BlockPos
	point: RayTraceVec3
	distance: number
	normal: RayTraceVec3
}

export interface CameraPixelRayViewMetadata {
	mirror?: boolean
	viewProjectionMatrix: ArrayLike<number>
	extrinsics: {
		cameraPosition: ArrayLike<number>
	}
	intrinsics: {
		width: number
		height: number
	}
}

const EPSILON = 1e-8

function sub(a: RayTraceVec3, b: RayTraceVec3): RayTraceVec3 {
	return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
}

function dot(a: RayTraceVec3, b: RayTraceVec3): number {
	return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
}

function cross(a: RayTraceVec3, b: RayTraceVec3): RayTraceVec3 {
	return [
		a[1] * b[2] - a[2] * b[1],
		a[2] * b[0] - a[0] * b[2],
		a[0] * b[1] - a[1] * b[0],
	]
}

function scaleAndAdd(a: RayTraceVec3, b: RayTraceVec3, scale: number): RayTraceVec3 {
	return [
		a[0] + b[0] * scale,
		a[1] + b[1] * scale,
		a[2] + b[2] * scale,
	]
}

function normalize(v: RayTraceVec3): RayTraceVec3 | null {
	const length = Math.hypot(v[0], v[1], v[2])
	if (length <= EPSILON) {
		return null
	}
	return [v[0] / length, v[1] / length, v[2] / length]
}

function vectorToTuple(v: Vector): RayTraceVec3 {
	return [v.x, v.y, v.z]
}

function blockPosToTuple(v: Vector): BlockPos {
	return [v.x, v.y, v.z]
}

function getQuadBlockPos(quad: Quad): BlockPos | null {
	for (const vertex of quad.vertices()) {
		if (vertex.blockPos !== undefined) {
			return blockPosToTuple(vertex.blockPos)
		}
	}
	return null
}

function intersectTriangle(
	origin: RayTraceVec3,
	direction: RayTraceVec3,
	a: RayTraceVec3,
	b: RayTraceVec3,
	c: RayTraceVec3,
): number | null {
	const edge1 = sub(b, a)
	const edge2 = sub(c, a)
	const pvec = cross(direction, edge2)
	const det = dot(edge1, pvec)
	if (Math.abs(det) <= EPSILON) {
		return null
	}

	const invDet = 1 / det
	const tvec = sub(origin, a)
	const u = dot(tvec, pvec) * invDet
	if (u < -EPSILON || u > 1 + EPSILON) {
		return null
	}

	const qvec = cross(tvec, edge1)
	const v = dot(direction, qvec) * invDet
	if (v < -EPSILON || u + v > 1 + EPSILON) {
		return null
	}

	const t = dot(edge2, qvec) * invDet
	return t > EPSILON ? t : null
}

export function rayTraceMeshes(meshes: Mesh[], ray: RayTraceRay): RayTraceHit | null {
	const direction = normalize(ray.direction)
	if (direction === null) {
		return null
	}

	let closest: RayTraceHit | null = null

	for (const mesh of meshes) {
		for (const quad of mesh.quads) {
			const blockPos = getQuadBlockPos(quad)
			if (blockPos === null) {
				continue
			}

			const vertices = quad.vertices().map(vertex => vectorToTuple(vertex.pos))
			const t1 = intersectTriangle(ray.origin, direction, vertices[0], vertices[1], vertices[2])
			const t2 = intersectTriangle(ray.origin, direction, vertices[0], vertices[2], vertices[3])
			const distance = t1 === null ? t2 : t2 === null ? t1 : Math.min(t1, t2)
			if (distance === null) {
				continue
			}
			if (closest !== null && distance >= closest.distance) {
				continue
			}

			closest = {
				blockPos,
				point: scaleAndAdd(ray.origin, direction, distance),
				distance,
				normal: vectorToTuple(quad.normal()),
			}
		}
	}

	return closest
}

export function createCameraPixelRay(
	viewMetadata: CameraPixelRayViewMetadata,
	x: number,
	y: number,
): RayTraceRay {
	const width = viewMetadata.intrinsics.width
	const height = viewMetadata.intrinsics.height
	if (width <= 0 || height <= 0) {
		throw new Error('Camera intrinsics must have positive width and height')
	}

	const localX = viewMetadata.mirror === true ? width - 1 - x : x
	const ndcX = ((localX + 0.5) / width) * 2 - 1
	const ndcY = 1 - ((y + 0.5) / height) * 2
	const viewProjectionMatrix = mat4.fromValues(
		viewMetadata.viewProjectionMatrix[0],
		viewMetadata.viewProjectionMatrix[1],
		viewMetadata.viewProjectionMatrix[2],
		viewMetadata.viewProjectionMatrix[3],
		viewMetadata.viewProjectionMatrix[4],
		viewMetadata.viewProjectionMatrix[5],
		viewMetadata.viewProjectionMatrix[6],
		viewMetadata.viewProjectionMatrix[7],
		viewMetadata.viewProjectionMatrix[8],
		viewMetadata.viewProjectionMatrix[9],
		viewMetadata.viewProjectionMatrix[10],
		viewMetadata.viewProjectionMatrix[11],
		viewMetadata.viewProjectionMatrix[12],
		viewMetadata.viewProjectionMatrix[13],
		viewMetadata.viewProjectionMatrix[14],
		viewMetadata.viewProjectionMatrix[15],
	)
	const inverseViewProjection = mat4.invert(mat4.create(), viewProjectionMatrix)
	if (!inverseViewProjection) {
		throw new Error('Unable to invert camera viewProjectionMatrix')
	}

	const worldPoint = vec4.transformMat4(
		vec4.create(),
		vec4.fromValues(ndcX, ndcY, -1, 1),
		inverseViewProjection,
	)
	if (Math.abs(worldPoint[3]) <= EPSILON) {
		throw new Error('Unable to unproject camera pixel')
	}

	const point: RayTraceVec3 = [
		worldPoint[0] / worldPoint[3],
		worldPoint[1] / worldPoint[3],
		worldPoint[2] / worldPoint[3],
	]
	const origin: RayTraceVec3 = [
		viewMetadata.extrinsics.cameraPosition[0],
		viewMetadata.extrinsics.cameraPosition[1],
		viewMetadata.extrinsics.cameraPosition[2],
	]
	const direction = normalize(sub(point, origin))
	if (direction === null) {
		throw new Error('Camera pixel ray has zero direction')
	}

	return { origin, direction }
}
